import { conditionText, patientConditions, relatedConditions } from './conditions';
import {
    baseCodeFor, codePresentation, csvDateTime, csvEnrollmentDate, duration, enrollmentPeriods,
    patientCancellationDates, patientEnrollmentEndings, rowExplanation, rowNotes, terminationExplanation,
    terminationEvidenceMessage
} from './presentation';
import { patientLabel, patientTableRows } from './rowSets';
import { CareRows } from './types';

const HEADERS = [
    'Organization ID', 'Service month', 'Row type', 'Patient', 'Hero user ID', 'Program', 'CPT code',
    'Program time (MM:SS)', 'Reported program time (MM:SS)', 'Time basis', 'Minimum (MM:SS)', 'Outcome', 'Units', 'Why',
    'Related conditions', 'Condition link details', 'Patient conditions', 'Enrolled', 'Disenrolled', 'Membership cancelled', 'Enrollment changes',
    'Patient changed this month', 'Patient last changed', 'Missing from minutes CSV', 'Notes'
];
const DATE_HEADERS = new Set(['Why', 'Enrolled', 'Disenrolled', 'Membership cancelled', 'Enrollment changes', 'Patient last changed']);

// Quoting alone does not neutralize formulas or cells starting with tab, CR or LF.
const csvCell = (value: string): string => {
    const protectedValue = /^(?:[\t\r\n]|[\s\u0000-\u001f\u007f-\u009f\u200b-\u200d\u2060]*[=+\-@])/u.test(value)
        ? `'${value}` : value;
    return `"${protectedValue.replace(/"/g, '""')}"`;
};

export const careCsv = (
    table: ReturnType<typeof patientTableRows>,
    month: string,
    organizationId: string | number,
    terminations: CareRows['termination_records'],
    terminationEvidence: CareRows['termination_evidence']
): string => {
    const output = [HEADERS.map((header) => DATE_HEADERS.has(header) ? `${header} (UTC)` : header)];
    table.sortedPatients.forEach(([userId, codes]) => {
        const allCodes = table.allPatientCodes.get(userId) ?? codes;
        const conditions = patientConditions(allCodes);
        const conditionList = [
            ...conditions.records.map(conditionText), ...(conditions.message ? [conditions.message] : [])
        ].join('\n');
        const cancellationDates = patientCancellationDates(allCodes, month, terminations, userId, csvEnrollmentDate).join('\n');
        const endings = patientEnrollmentEndings(allCodes, month, terminations, csvEnrollmentDate).join('\n');
        const changedRow = table.changedPatients.get(userId);
        codes.forEach((row) => {
            const display = codePresentation(row);
            const periods = enrollmentPeriods(row, terminations);
            const missingEnrollment = display.reportedOnly
                ? 'No enrollment found for this month' : 'Enrollment dates not recorded';
            const matched = row.is_current ? row.current_evidence.facts?.coverage?.matched_presence : undefined;
            const related = relatedConditions(row, allCodes);
            const reportedSeconds = matched === true ? row.current_evidence.facts?.raw_seconds : null;
            output.push([
                String(organizationId), month, 'CPT', patientLabel(row), String(userId), row.program, row.code,
                display.seconds == null ? 'Unavailable' : duration(display.seconds),
                reportedSeconds == null ? 'Unavailable' : duration(reportedSeconds),
                display.timeBasis,
                row.required_seconds == null ? 'Unavailable' : duration(row.required_seconds),
                display.status,
                display.unitsValue,
                rowExplanation(row, month, baseCodeFor(row, table.rowsById), {
                    date: csvEnrollmentDate, dateTime: csvDateTime
                }),
                related.items.join('\n'), related.message, conditionList,
                periods.length ? periods.map((period) => period.enrolledAt ? csvEnrollmentDate(period.enrolledAt) : '').join('\n') : missingEnrollment,
                periods.map((period) => period.disenrolledAt ? csvEnrollmentDate(period.disenrolledAt) : '').join('\n'),
                cancellationDates, endings, changedRow ? 'Yes' : 'No',
                changedRow ? csvDateTime(changedRow.changes.changed_at) : '',
                matched === false ? 'Yes' : matched === true ? 'No' : 'Unavailable', rowNotes(row).join('\n\n')
            ]);
        });
    });
    table.sortedEndedPatients.forEach(([userId, records]) => records.forEach((record) => {
        output.push([
            String(organizationId), month, 'Information only',
            patientLabel({ patient_name: null, hero_user_id: userId }), String(userId), record.program, '',
            '', '', '', '', '', '', terminationExplanation(record, csvEnrollmentDate), '', '', '',
            csvEnrollmentDate(record.enrolled_at),
            record.termination_reason === 'program_disenrollment' ? csvEnrollmentDate(record.terminated_at) : '',
            patientCancellationDates([], month, records, userId, csvEnrollmentDate).join('\n'),
            `Ended: ${csvEnrollmentDate(record.terminated_at)}`, '', '', '', terminationEvidenceMessage(terminationEvidence) ?? ''
        ]);
    }));
    // UTF-8 BOM lets spreadsheet applications retain patient names and punctuation.
    return `\uFEFF${output.map((record) => record.map(csvCell).join(',')).join('\r\n')}\r\n`;
};

export const downloadCareCsv = (content: string, month: string, organizationId: string | number): void => {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    try {
        anchor.href = url;
        anchor.download = `care-billing-org-${organizationId}-${month}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
    } finally {
        anchor.remove();
        // Give the browser time to begin reading the blob before releasing it.
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
};
